import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { MenuBook } from '@mui/icons-material';
import { ROLES, SchoolActivity } from '@shared/types';
import ReadingSessionsKPIs from '../../components/admin/reading-sessions/ReadingSessionsKPIs';
import ReadingSessionsCharts from '../../components/admin/reading-sessions/ReadingSessionsCharts';
import ReadingSessionsTable from '../../components/admin/reading-sessions/ReadingSessionsTable';
import { getReadingSessionsKPIs, getReadingSessionsTrends, getReadingSessionsDistributions, getReadingSessionsList } from '../../../services/api/admin.service';

interface KPIData {
  totalSessions: number;
  averageDuration: number;
  averageAccuracy: number;
  systemCrashRate: number;
  abandonmentRate: number;
  completedSessions: number;
  crashedSessions: number;
  abandonedSessions: number;
}

interface TrendData {
  date: string;
  sessions: number;
  averageAccuracy: number;
  averageDuration: number;
}

interface DistributionData {
  byStatus: Array<{ name: string; value: number }>;
  byDevice: Array<{ name: string; value: number }>;
  byBrowser: Array<{ name: string; value: number }>;
}

const ReadingSessionsDashboardPage = observer(() => {
  const { t, i18n } = useTranslation();
  const adminStore = useStoreByName('adminStore');
  const userStore = useStoreByName('userStore');
  const isRtl = i18n.language === 'he';

  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;

  const [kpis, setKpis] = useState<KPIData | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [distributions, setDistributions] = useState<DistributionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [daysAgo, setDaysAgo] = useState(30);

  useEffect(() => {
    loadData();
  }, [adminStore.selectedSchoolId, daysAgo]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const schoolId = adminStore.selectedSchoolId || undefined;
      
      const [kpisData, trendsData, distributionsData] = await Promise.all([
        getReadingSessionsKPIs(schoolId, daysAgo),
        getReadingSessionsTrends(schoolId, daysAgo),
        getReadingSessionsDistributions(schoolId, daysAgo),
      ]);

      setKpis(kpisData);
      setTrends(trendsData);
      setDistributions(distributionsData);
    } catch (error) {
      console.error('Error loading reading sessions data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="statistics-container" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="head-line">
        <h1 className="title">
          {t('readingSessionsAnalytics')} <MenuBook />
        </h1>
        <p className="subtitle">
          {adminStore.selectedSchoolId ? (
            <>
              {t('showingDataFor')} {adminStore.availableSchools.find((s: SchoolActivity) => String(s.id) === String(adminStore.selectedSchoolId))?.name || ''}
            </>
          ) : (
            <>
              {isPrincipal ? t('showingDataForYourSchools') : t('showingDataForAllSchools')}
            </>
          )}
        </p>
      </div>

      {/* Date Range Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('timeRange')}</InputLabel>
          <Select
            value={daysAgo}
            label={t('timeRange')}
            inputProps={{ 'aria-label': 'טווח זמן' }}
            onChange={(e) => setDaysAgo(Number(e.target.value))}
          >
            <MenuItem value={7}>{t('last7Days')}</MenuItem>
            <MenuItem value={30}>{t('last30Days')}</MenuItem>
            <MenuItem value={90}>{t('last90Days')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <div className="loadingContainer">
          <CircularProgress size={60} style={{ color: '#667eea' }} />
        </div>
      ) : (
        <>
          {/* KPIs Section */}
          <ReadingSessionsKPIs kpis={kpis} isLoading={isLoading} />

          {/* Charts Section */}
          <ReadingSessionsCharts 
            trends={trends} 
            distributions={distributions}
            isLoading={isLoading}
          />

          {/* Table Section */}
          <ReadingSessionsTable 
            schoolId={adminStore.selectedSchoolId || undefined}
            daysAgo={daysAgo}
          />
        </>
      )}
    </div>
  );
});

export default ReadingSessionsDashboardPage;

